import { useState } from 'react';

import { Navigate } from '@tanstack/react-router';

import { MapPin, MoreHorizontal, Plus } from 'lucide-react';

import { Button, cn } from '@src/components/ui';
import { useActiveOrg, usePermissions } from '@src/context/hooks';
import { Form, TextField } from '@src/corelib/forms/generic';
import {
  useLocationDeleteMutation,
  useLocationSaveMutation,
  useLocationSetDefaultMutation,
  useLocationsQuery,
} from '@src/query';

import { Toaster, useToasts } from '../Listings/useToasts';
import { ConfirmDialog, firstApiError, Modal } from './controls';
import { locationDefaults, locationSchema } from './locationForm';
import PageHeader from './PageHeader';


/** Row ⋯ menu item — a left-aligned, full-width ghost Button styled as a list row. */
const MENU_ITEM = 'h-auto justify-start rounded-none px-3.5 py-2 text-[13px] font-medium hover:bg-surface-2';


function addressLine(location) {
  return [location.address, location.city, [location.region, location.postal_code].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
}


/** Single location card with a default badge and an actions menu (set default /
 * delete). The menu is a small popover; closing handled by the page. */
function LocationRow(props) {
  const { location, canWrite, menuOpen, onMenuToggle, onSetDefault, onEdit, onDelete } = props;
  const isDefault = Boolean(location.is_default);
  const address = addressLine(location);
  const partCount = location.part_count;

  return (
    <div className="relative flex items-center gap-3.5 rounded-[11px] border border-hairline bg-surface-1 px-4 py-3.5">
      <span
        className={cn(
          'grid size-10 shrink-0 place-items-center rounded-[10px] bg-surface-2',
          isDefault ? 'text-primary' : 'text-ink-subtle',
        )}
      >
        <MapPin className="size-[18px]" />
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-ink">{location.name}</span>

          {isDefault
            ? (
              <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">
                Default
              </span>
            )
            : null}
        </span>

        {address
          ? <span className="truncate text-xs text-ink-subtle">{address}</span>
          : null}
      </span>

      {typeof partCount === 'number'
        ? <span className="shrink-0 text-[13px] font-medium text-ink-muted">{`${partCount} parts`}</span>
        : null}

      {canWrite
        ? (
          <Button
            size="icon"
            type="button"
            variant="ghost"
            aria-label={`Actions for ${location.name}`}
            className="size-8 shrink-0 rounded-[7px] bg-surface-2 text-ink-muted"
            onClick={onMenuToggle}
          >
            <MoreHorizontal className="size-4" />
          </Button>
        )
        : null}

      {menuOpen
        ? (
          <div
            className={cn(
              'absolute right-3 top-[52px] z-10 flex w-44 flex-col rounded-lg border border-hairline',
              'bg-surface-1 py-1 shadow-xl',
            )}
          >
            {!isDefault
              ? (
                <Button
                  type="button"
                  variant="ghost"
                  className={cn(MENU_ITEM, 'text-ink')}
                  onClick={onSetDefault}
                >
                  Set as default
                </Button>
              )
              : null}

            <Button type="button" variant="ghost" className={cn(MENU_ITEM, 'text-ink')} onClick={onEdit}>
              Edit
            </Button>

            <Button
              type="button"
              variant="ghost"
              className={cn(MENU_ITEM, 'text-danger hover:text-danger')}
              onClick={onDelete}
            >
              Delete
            </Button>
          </div>
        )
        : null}
    </div>
  );
}

LocationRow.displayName = 'LocationRow';


/** Add / edit a location. The `<Form>` carries an `id` so the footer's submit
 * Button (which lives outside the FormProvider) can target it via `form=`. */
function LocationEditor({ location, busy, onSubmit, onError, onClose }) {
  const editing = Boolean(location);

  return (
    <Modal
      title={editing ? 'Edit location' : 'Add location'}
      onClose={onClose}
      footer={(
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>

          <Button type="submit" loading={busy} form="location-form">
            {editing ? 'Save' : 'Add location'}
          </Button>
        </>
      )}
    >
      <Form
        id="location-form"
        schema={locationSchema}
        defaultValues={locationDefaults(location)}
        onError={onError}
        onSubmit={onSubmit}
      >
        <TextField name="name" label="Location name" placeholder="Main Yard" />
        <TextField name="address" label="Street address" />

        <div className="flex flex-col gap-3.5 sm:flex-row">
          <TextField name="city" label="City" className="flex-[2]" />
          <TextField name="region" label="State" className="flex-1" />
          <TextField label="ZIP" name="postal_code" className="flex-1" />
        </div>
      </Form>
    </Modal>
  );
}

LocationEditor.displayName = 'LocationEditor';


/** Locations (#169, .pen g4qa0): the org's stocking yards — default badge,
 * add/edit, set-default, delete (blocked when parts still live there).
 * Owner/Manager only; other roles are redirected. */
function Locations() {
  const { toasts, push } = useToasts();
  const { seller } = useActiveOrg();
  const { canManageOrg: canManage } = usePermissions();

  const locationsQuery = useLocationsQuery(seller?.id, { enabled: Boolean(seller) });
  const queryKey = locationsQuery.queryKey;

  const [editorState, setEditorState] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);

  const saveMutation = useLocationSaveMutation();
  const setDefaultMutation = useLocationSetDefaultMutation();
  const deleteMutation = useLocationDeleteMutation();

  if (!seller) {
    return null;
  }

  if (!canManage) {
    return <Navigate replace to="/app/settings" />;
  }

  const locations = locationsQuery.data || [];

  const openEditor = (location) => {
    setOpenMenuId(null);
    setEditorState({ location });
  };

  // Returns the mutateAsync promise so the Form engine can map 400 field errors
  // back onto the inputs (and fire onError for server/no-response failures).
  const submitEditor = (values) => {
    const locationId = editorState?.location?.id;
    return saveMutation
      .mutateAsync({ sellerId: seller.id, locationId, payload: values, queryKey })
      .then(() => {
        setEditorState(null);
        push(locationId ? 'Location updated' : 'Location added');
      });
  };

  const requestDelete = (location) => {
    setOpenMenuId(null);
    setPendingDelete(location);
  };

  const setDefault = (location) => {
    setOpenMenuId(null);
    setDefaultMutation.mutate(
      { sellerId: seller.id, locationId: location.id, queryKey },
      {
        onSuccess: () => push(`${location.name} is now the default`),
        onError: () => push('Could not set default location', 'danger'),
      },
    );
  };

  const confirmDelete = () => {
    deleteMutation.mutate(
      { sellerId: seller.id, locationId: pendingDelete.id, queryKey },
      {
        onSuccess: () => {
          setPendingDelete(null);
          push('Location deleted');
        },
        onError: (error) => {
          setPendingDelete(null);
          push(firstApiError(error?.response?.data) || 'Could not delete location', 'danger');
        },
      },
    );
  };

  const addButton = (className) => (
    <Button
      type="button"
      className={cn('gap-1.5 text-[13px]', className)}
      onClick={() => openEditor(null)}
    >
      <Plus className="size-[15px]" />
      Add location
    </Button>
  );

  return (
    <div className="flex flex-1 flex-col gap-5 lg:p-7">
      <PageHeader
        title="Locations"
        subtitle="Each part lives at one location. Switch from the top bar."
        actions={addButton('h-[38px]')}
      />

      <div className="flex w-full flex-col gap-2.5 px-4 pb-6 lg:px-0">
        {addButton('h-11 lg:hidden')}

        {locationsQuery.isPending
          ? <span className="py-10 text-center text-[13px] text-ink-subtle">Loading locations…</span>
          : locationsQuery.isError
            ? <span className="py-10 text-center text-[13px] text-danger">Could not load locations.</span>
            : locations.length === 0
              ? <span className="py-10 text-center text-[13px] text-ink-muted">No locations yet.</span>
              : locations.map((location) => (
                <LocationRow
                  key={location.id}
                  canWrite={canManage}
                  menuOpen={openMenuId === location.id}
                  location={location}
                  onEdit={() => openEditor(location)}
                  onDelete={() => requestDelete(location)}
                  onSetDefault={() => setDefault(location)}
                  onMenuToggle={() => setOpenMenuId((current) => (current === location.id ? null : location.id))}
                />
              ))}
      </div>

      {editorState
        ? (
          <LocationEditor
            busy={saveMutation.isPending}
            onSubmit={submitEditor}
            location={editorState.location}
            onClose={() => setEditorState(null)}
            onError={(error, meta) => {
              if (!meta?.isInvalid) {
                push('Could not save location', 'danger');
              }
            }}
          />
        )
        : null}

      {pendingDelete
        ? (
          <ConfirmDialog
            busy={deleteMutation.isPending}
            title={`Delete ${pendingDelete.name}?`}
            onConfirm={confirmDelete}
            onCancel={() => setPendingDelete(null)}
            body="Locations with parts still assigned cannot be deleted. Move those parts first."
          />
        )
        : null}

      <Toaster toasts={toasts} />
    </div>
  );
}

Locations.displayName = 'Locations';
export default Locations;
